import {
  SANDBOX_BRAND_STUDIO_DRAFTS_DIR,
  SANDBOX_BRAND_STUDIO_DRAFTS_PATH,
  SANDBOX_DEFAULT_BRAND_PATH,
  SANDBOX_INSIGHTS_PATH,
} from "replo-utils/lib/sandboxPaths";
import { emailTemplateTypeSchema } from "schemas/email";
import { insightsFileNameSchema } from "schemas/insightsDashboard/document";
import { z } from "zod";

import { registryItemTypeSchema, registrySlugSchema } from "../registry";
import { viewSchema } from "../views";
import { mcpUploadAssetsOutputSchema } from "./replo/bedrockMcp";

export const reploOpencodeHarnessToolIconKeyByName = {
  "capture-html": "search",
  "collect-site-report-evidence": "search",
  "compile-email": "publish",
  "create-registry-preview": "imageUpload",
  "dev-control": "terminal",
  "download-registry-items": "file",
  "find-design-inspiration": "search",
  "find-registry-items": "search",
  "install-staged-registry-items": "file",
  "manage-tabs": "link",
  "pin-brand-imagery": "imageUpload",
  publish: "publish",
  "publish-email": "publish",
  "scrape-branding": "search",
  "set-project-context": "file",
  "suggest-followups": "list",
  "upload-registry-items": "publish",
  "upload-asset": "imageUpload",
  "upload-assets": "imageUpload",
  "validate-insights-report": "search",
} as const;

export const reploOpencodeHarnessCompileEmailToolInputSchema = z
  .object({
    type: emailTemplateTypeSchema.describe(
      "The email type whose `.email/<type>.email.tsx` source to compile and save as the draft (e.g. 'order.confirmation').",
    ),
  })
  .describe("ReploOpencodeHarnessCompileEmailToolInput");

export const reploOpencodeHarnessCompileEmailToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessCompileEmailToolOutput");

export const reploOpencodeHarnessPublishEmailToolInputSchema = z
  .object({
    type: emailTemplateTypeSchema.describe(
      "The email type whose compiled draft to publish (e.g. 'order.confirmation'). Run compile_email first — publishing releases the saved draft, not the .tsx source.",
    ),
  })
  .describe("ReploOpencodeHarnessPublishEmailToolInput");

export const reploOpencodeHarnessPublishEmailToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessPublishEmailToolOutput");

// Shared by the sandbox `publish` tool and the public `publish_site` MCP tool so
// the two surfaces describe the same operation identically.
export const REPLO_OPENCODE_HARNESS_PUBLISH_TOOL_DESCRIPTION =
  "Publish a site to make changes live. Requires the siteId from list_sites. Builds, packages, uploads, and deploys.";

export const reploOpencodeHarnessPublishToolInputSchema = z
  .object({
    siteId: z
      .uuid()
      .describe(
        "The ID of the site to publish. Get this from list_sites. The tool finds the site's folder on disk itself, so there is nothing else to look up.",
      ),
  })
  .describe("ReploOpencodeHarnessPublishToolInput");

export const reploOpencodeHarnessPublishToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessPublishToolOutput");

export const reploOpencodeHarnessUploadAssetToolInputSchema = z
  .object({
    filePath: z.string(),
    mimeType: z.string().optional(),
    name: z.string().optional(),
  })
  .describe("ReploOpencodeHarnessUploadAssetToolInput");

export const reploOpencodeHarnessUploadAssetToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessUploadAssetToolOutput");

const reploOpencodeHarnessUploadAssetItemSchema = z
  .object({
    r2Uri: z.string().optional(),
    url: z.url().optional(),
    dataBase64: z.string().optional(),
    mimeType: z.string().optional(),
    name: z.string().min(1).optional(),
    altText: z.string().min(1).optional(),
    isIndexed: z.boolean().optional(),
    tags: z.array(z.string().min(1)).max(16).optional(),
    indexingHint: z.string().max(2000).optional(),
    generateEmbedding: z.boolean().optional(),
  })
  .refine((value) => {
    return (
      [value.r2Uri, value.url, value.dataBase64].filter((source) => {
        return Boolean(source);
      }).length === 1
    );
  }, "Each asset must include exactly one source field");

export const reploOpencodeHarnessUploadAssetsToolInputSchema = z
  .object({
    brandName: z
      .string()
      .min(1)
      .describe("Brand slug to apply as a tag to every uploaded asset."),
    saveDirectory: z
      .enum(["assets", "fonts"])
      .default("assets")
      .optional()
      .describe("Logical upload group. Fonts get an additional font tag."),
    refererUrl: z.url().optional(),
    assets: z.array(reploOpencodeHarnessUploadAssetItemSchema).min(1).max(50),
  })
  .describe("ReploOpencodeHarnessUploadAssetsToolInput");

export const reploOpencodeHarnessUploadAssetsToolOutputSchema =
  mcpUploadAssetsOutputSchema.describe(
    "ReploOpencodeHarnessUploadAssetsToolOutput",
  );

export const reploOpencodeHarnessCreateRegistryPreviewToolInputSchema = z
  .object({
    artifactPath: z
      .string()
      .describe(
        "Absolute or workspace-relative path to the .registry.json artifact. Must already have slug, name, type, visibility, and files. id may be empty; the tool will mint a UUID into it.",
      ),
    baseUrl: z
      .url()
      .describe(
        "Dev server base URL, for example http://127.0.0.1:3000. Must already be reachable; the tool does not start a server.",
      ),
    pages: z
      .array(
        z.object({
          key: z.string().min(1),
          route: z.string().min(1),
        }),
      )
      .min(1)
      .max(50)
      .describe(
        "Routes to capture. For page/section artifacts pass exactly one entry. For site artifacts pass one entry per shipped route.",
      ),
    canonicalPageKey: z
      .string()
      .min(1)
      .optional()
      .describe(
        "Page key whose preview becomes the artifact's top-level previewHtml/previewUrls. Defaults to pages[0].key.",
      ),
  })
  .describe("ReploOpencodeHarnessCreateRegistryPreviewToolInput");

export const reploOpencodeHarnessCreateRegistryPreviewToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessCreateRegistryPreviewToolOutput");

export const reploOpencodeHarnessCaptureHtmlToolInputSchema = z
  .object({
    url: z
      .string()
      .describe(
        "Base URL for the local dev server, for example http://127.0.0.1:3122.",
      ),
    route: z
      .string()
      .describe("Route to capture, for example /collections/wallets."),
    devPath: z
      .string()
      .optional()
      .describe(
        "Optional app directory to start if the URL is not already healthy.",
      ),
    startCommand: z
      .string()
      .optional()
      .describe(
        "Optional command to start from devPath, for example pnpm dev.",
      ),
  })
  .describe("ReploOpencodeHarnessCaptureHtmlToolInput");

export const reploOpencodeHarnessCaptureHtmlToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessCaptureHtmlToolOutput");

export const reploOpencodeHarnessPinBrandImageryToolInputSchema = z
  .object({
    workingDirectory: z
      .string()
      .min(1)
      .describe(
        `Workspace-relative Brand Studio draft directory, e.g. ${SANDBOX_BRAND_STUDIO_DRAFTS_DIR}/hexclad.`,
      ),
  })
  .describe("ReploOpencodeHarnessPinBrandImageryToolInput");

export const reploOpencodeHarnessPinBrandImageryToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessPinBrandImageryToolOutput");

export const reploOpencodeHarnessScrapeBrandingToolInputSchema = z
  .object({
    url: z.url().describe("Public homepage URL to scrape."),
    workingDirectory: z
      .string()
      .min(1)
      .describe(
        `Workspace-relative Brand Studio draft directory, e.g. ${SANDBOX_BRAND_STUDIO_DRAFTS_DIR}/hexclad. The tool writes the full brand tree here.`,
      ),
    maxAge: z
      .number()
      .int()
      .nonnegative()
      .optional()
      .describe("Scrape cache freshness window in ms (default 172800000)."),
  })
  .describe("ReploOpencodeHarnessScrapeBrandingToolInput");

export const reploOpencodeHarnessScrapeBrandingToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessScrapeBrandingToolOutput");

export const reploOpencodeHarnessSetProjectContextToolInputSchema = z
  .object({
    draftSlug: z
      .string()
      .min(1)
      .describe(
        `Brand Studio draft slug under ${SANDBOX_BRAND_STUDIO_DRAFTS_PATH} to promote into ${SANDBOX_DEFAULT_BRAND_PATH} and set as the project's primary brand.`,
      ),
  })
  .describe("ReploOpencodeHarnessSetProjectContextToolInput");

export const reploOpencodeHarnessSetProjectContextToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessSetProjectContextToolOutput");

export const reploOpencodeHarnessValidateInsightsReportToolInputSchema = z
  .object({
    fileName: insightsFileNameSchema.describe(
      `Top-level report filename under ${SANDBOX_INSIGHTS_PATH}, e.g. "triple-whale.insights.json".`,
    ),
  })
  .describe("ReploOpencodeHarnessValidateInsightsReportToolInput");

export const reploOpencodeHarnessValidateInsightsReportToolOutputSchema = z
  .object({
    valid: z.boolean().describe("Whether the report passed validation."),
    filePath: z
      .string()
      .describe("Absolute path of the report that was validated."),
    summary: z
      .string()
      .describe("One-line result the agent can relay or act on."),
    issues: z
      .array(
        z.object({
          path: z
            .string()
            .describe(
              `Dotted path to the offending value, e.g. "body.4.body".`,
            ),
          message: z.string().describe("Why the value is invalid."),
          code: z
            .string()
            .describe(
              "Issue code, e.g. invalid_type or unknown_scope_placeholder.",
            ),
        }),
      )
      .describe("Violations to fix; omitted when valid.")
      .optional(),
  })
  .describe("ReploOpencodeHarnessValidateInsightsReportToolOutput");

export const reploOpencodeHarnessDownloadRegistryItemsToolInputSchema = z
  .object({
    items: z
      .array(
        z
          .object({
            id: z
              .string()
              .optional()
              .describe(
                "Registry item ID to download. Prefer this when you already have one.",
              ),
            slug: z
              .string()
              .optional()
              .describe(
                "Registry item slug to download. The tool will resolve it to an id.",
              ),
            type: registryItemTypeSchema
              .optional()
              .describe(
                "Registry item type, when known. Supplying this with id lets the tool skip the metadata lookup.",
              ),
          })
          .refine(
            (value) => {
              return Boolean(value.id) || Boolean(value.slug);
            },
            { message: "Each item must include either id or slug." },
          ),
      )
      .min(1)
      .max(5)
      .describe(
        "Up to 5 items per call. Each is fetched in parallel and either installed in place (skill, plan, branding) or staged for the install-registry-items skill to lift into place (page, section, site).",
      ),
  })
  .describe("ReploOpencodeHarnessDownloadRegistryItemsToolInput");

export const reploOpencodeHarnessDownloadRegistryItemsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessDownloadRegistryItemsToolOutput");

const stagedRegistryItemBaseSchema = z.object({
  id: z
    .string()
    .optional()
    .describe("Registry item ID from download-registry-items."),
  slug: registrySlugSchema.describe(
    "Registry item slug from download-registry-items.",
  ),
  stagedAt: z
    .string()
    .describe(
      "Absolute path to the staged item directory returned by download-registry-items.",
    ),
  dependencies: z
    .array(z.string())
    .default([])
    .describe("NPM package names declared by the registry item."),
});

export const reploOpencodeHarnessInstallStagedRegistryItemsToolInputSchema = z
  .object({
    projectRoot: z
      .string()
      .optional()
      .describe(
        "Next.js project root — the absolute root of the site you're targeting (e.g. `/workspace/dev/<directory>`). In the sandbox, defaults to the active site via PROJECT_WEBSITE_DIR.",
      ),
    items: z
      .array(
        z.discriminatedUnion("type", [
          stagedRegistryItemBaseSchema.extend({
            type: z.literal("page"),
            placement: z
              .object({
                routeDestRel: z
                  .string()
                  .optional()
                  .describe(
                    "Project-root-relative destination for page.tsx. Defaults from the staged route.",
                  ),
              })
              .default({}),
          }),
          stagedRegistryItemBaseSchema.extend({
            type: z.literal("section"),
            placement: z.object({
              parentPageAbs: z
                .string()
                .describe(
                  "Absolute path to the page that should render the section.",
                ),
              insertPosition: z
                .string()
                .describe(
                  "Textual placement hint, for example 'after <Hero />'.",
                ),
              entry: z
                .string()
                .optional()
                .describe(
                  "Exported section component name. Inferred if omitted.",
                ),
            }),
          }),
          stagedRegistryItemBaseSchema.extend({
            type: z.literal("site"),
            placement: z
              .object({
                sitePrefix: z
                  .string()
                  .optional()
                  .describe("Route prefix under app/. Defaults to dev/<slug>."),
              })
              .default({}),
          }),
        ]),
      )
      .min(1)
      .max(5)
      .describe(
        "Staged page, section, or site registry items to lift into the project.",
      ),
  })
  .describe("ReploOpencodeHarnessInstallStagedRegistryItemsToolInput");

export const reploOpencodeHarnessInstallStagedRegistryItemsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessInstallStagedRegistryItemsToolOutput");

const installRegistryItemBaseSchema = z.object({
  id: z.string().min(1).describe("Registry item ID to install."),
});

export const reploOpencodeHarnessInstallRegistryItemsToolInputSchema = z
  .object({
    projectRoot: z
      .string()
      .min(1)
      .nullable()
      .default(null)
      .describe(
        "Absolute or workspace-relative Next.js project root for page and site installs. Null defaults to PROJECT_WEBSITE_DIR, then sandbox site-checkout discovery; required on local.",
      ),
    siteId: z
      .uuid()
      .nullable()
      .default(null)
      .describe(
        "Target site for page and site installs. The coordinator resolves it to that site's sandbox checkout; null falls back to the project's default site. Ignored when projectRoot is set.",
      ),
    items: z
      .array(
        z.discriminatedUnion("type", [
          installRegistryItemBaseSchema.extend({
            type: z.literal("skill"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("plan"),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("page"),
            routeDestRel: z
              .string()
              .min(1)
              .nullable()
              .default(null)
              .describe(
                "Project-root-relative destination for the page route file. Null derives it from the staged route, suffixing on collision.",
              ),
          }),
          installRegistryItemBaseSchema.extend({
            type: z.literal("site"),
            sitePrefix: z
              .string()
              .min(1)
              .nullable()
              .default(null)
              .describe(
                "Route prefix under app/. Null defaults to dev/<slug>.",
              ),
          }),
        ]),
      )
      .min(1)
      .max(5)
      .describe(
        "Mechanical installs only: skill, plan, page, and site are a deterministic download-and-lift with zero placement judgment. Section, branding, and product installs need the agent and stay on the chat flow.",
      ),
  })
  .describe("ReploOpencodeHarnessInstallRegistryItemsToolInput");

// Only page and site installs write into a site checkout; skill and plan
// installs land in durable dirs. Shared so the coordinator's checkout
// resolution and the CLI's stay in lockstep.
export function installRegistryItemNeedsSiteCheckout(item: {
  type: z.infer<
    typeof reploOpencodeHarnessInstallRegistryItemsToolInputSchema
  >["items"][number]["type"];
}): boolean {
  return item.type === "page" || item.type === "site";
}

export const reploOpencodeHarnessInstallRegistryItemsToolOutputSchema = z
  .object({
    installed: z.array(
      z.discriminatedUnion("type", [
        z.object({
          type: z.enum(["skill", "plan"]),
          id: z.string().min(1),
          slug: z.string().min(1),
          destination: z
            .string()
            .min(1)
            .describe(
              "Canonical filesystem destination the item was written to.",
            ),
        }),
        z.object({
          type: z.enum(["page", "site"]),
          id: z.string().min(1),
          slug: z.string().min(1),
          routeUrl: z
            .string()
            .nullable()
            .describe(
              "Rendered route the lift produced; null when no route file was lifted.",
            ),
        }),
      ]),
    ),
    failed: z
      .array(
        z.object({
          id: z.string().min(1),
          error: z.string().min(1),
          errorClass: z.string().min(1).optional(),
        }),
      )
      .describe(
        "Every requested item appears in exactly one of installed or failed.",
      ),
  })
  .describe("ReploOpencodeHarnessInstallRegistryItemsToolOutput");

export const reploOpencodeHarnessInstallRegistryItemsRpcOutputSchema = z
  .discriminatedUnion("status", [
    reploOpencodeHarnessInstallRegistryItemsToolOutputSchema.extend({
      status: z.literal("installed"),
    }),
    z.object({
      status: z.literal("requires_site"),
      siteId: z.uuid().nullable(),
    }),
    z.object({
      status: z.literal("outcome_unknown"),
    }),
  ])
  .describe("ReploOpencodeHarnessInstallRegistryItemsRpcOutput");

export const reploOpencodeHarnessFindRegistryItemsToolInputSchema = z
  .object({
    id: z.string().optional().describe("Registry item ID to look up."),
    slug: z.string().optional().describe("Registry item slug to look up."),
    type: registryItemTypeSchema
      .optional()
      .describe(
        "Registry item type filter (site, page, section, skill, plan, branding, product, reference).",
      ),
    search: z
      .string()
      .max(200)
      .optional()
      .describe(
        "Optional semantic search query for matching items (200 characters max).",
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(20)
      .optional()
      .describe(
        "Optional cap on the number of items returned (1-20, default 1).",
      ),
  })
  .describe("ReploOpencodeHarnessFindRegistryItemsToolInput");

export const reploOpencodeHarnessFindRegistryItemsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessFindRegistryItemsToolOutput");

export const reploOpencodeHarnessFindDesignInspirationToolInputSchema = z
  .object({
    search: z
      .string()
      .max(200)
      .describe(
        "Semantic search query for the design direction — phrase by look and intent (e.g. 'playful maximalist snack brand homepage', 'clinical wellness product page').",
      ),
    pageType: z
      .string()
      .optional()
      .describe(
        "Optional page-type filter (e.g. home-landing, product-detail, collection, pricing, about-story).",
      ),
    limit: z
      .number()
      .int()
      .min(1)
      .max(20)
      .optional()
      .describe(
        "Optional cap on the number of references returned (1-20, default 5).",
      ),
  })
  .describe("ReploOpencodeHarnessFindDesignInspirationToolInput");

export const reploOpencodeHarnessFindDesignInspirationToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessFindDesignInspirationToolOutput");

export const reploOpencodeHarnessSuggestFollowupsToolArgsShape = {
  suggestions: z
    .array(
      z
        .string()
        .min(1)
        .max(120)
        .describe(
          "A next step curated for this specific business and aimed at a concrete business outcome (revenue, conversion, average order value, retention, or trust). It is sent to the agent as the USER'S OWN message when clicked, so write it in the user's voice: an imperative or first-person request (e.g. 'Add customer reviews to my homepage to build trust'), using 'my'/'the' not 'your', never phrased as the agent addressing or asking the user, and never a question. Becomes both the chip label and the prefilled message, so keep it to roughly 10 words.",
        ),
    )
    .min(1)
    .max(3)
    .describe(
      "1-3 suggested next steps, curated for this business and ordered by likely business impact (most valuable first).",
    ),
};

export const reploOpencodeHarnessSuggestFollowupsToolInputSchema = z
  .object(reploOpencodeHarnessSuggestFollowupsToolArgsShape)
  .describe("ReploOpencodeHarnessSuggestFollowupsToolInput");

export const reploOpencodeHarnessSuggestFollowupsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessSuggestFollowupsToolOutput");

export const reploOpencodeHarnessCollectSiteReportEvidenceToolArgsShape = {
  route: z
    .string()
    .min(1)
    .describe("The single page route to scan in this call."),
  pageBaseUrl: z
    .string()
    .describe("The sandbox dev server origin the routes render on."),
  siteDirectory: z
    .string()
    .describe("Absolute path of the site source root inside the sandbox."),
};

export const reploOpencodeHarnessCollectSiteReportEvidenceToolInputSchema = z
  .object(reploOpencodeHarnessCollectSiteReportEvidenceToolArgsShape)
  .describe("ReploOpencodeHarnessCollectSiteReportEvidenceToolInput");

export const reploOpencodeHarnessCollectSiteReportEvidenceToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessCollectSiteReportEvidenceToolOutput");

export const reploOpencodeHarnessTabTargetSchema = z.discriminatedUnion(
  "type",
  [
    z.object({
      type: z.literal("miniapp"),
      miniappId: z
        .string()
        .min(1)
        .describe(
          'Miniapp ID for the tab target. Use "website-builder" for the user website preview.',
        ),
      miniappRoute: z
        .string()
        .min(1)
        .nullable()
        .describe(
          'Route inside the miniapp tab. For website-builder pages, use a public route like "/collections/wallets"; use null for a miniapp root tab.',
        ),
    }),
  ],
);

export const reploOpencodeHarnessDevControlToolInputSchema = z
  .object({
    command: z
      .enum(["provision", "start", "stop"])
      .describe(
        "Lifecycle verb for the site. `provision` clones and installs the site without starting it; `start` brings the site's dev server up; `stop` tears down the dev server for that site.",
      ),
    siteId: z
      .uuid()
      .describe(
        "The site's ID. Read it from the site's package.json `replo.siteId`, or get it from list_sites.",
      ),
  })
  .describe("ReploOpencodeHarnessDevControlToolInput");

export const reploOpencodeHarnessDevControlToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessDevControlToolOutput");

export const reploOpencodeHarnessOpenAppToolInputSchema = z
  .object({
    route: z
      .string()
      .describe(
        "Public URL path to open in the editor, for example /collections/wallets.",
      ),
    appName: z
      .string()
      .optional()
      .describe(
        "Optional app name when more than one app is rendered in the sandbox.",
      ),
  })
  .describe("ReploOpencodeHarnessOpenAppToolInput");

export const reploOpencodeHarnessOpenAppToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessOpenAppToolOutput");

export const reploOpencodeHarnessManageTabsToolInputSchema = z
  .discriminatedUnion("operation", [
    z.object({
      operation: z
        .literal("open")
        .describe(
          "Open the selected view. By default it opens in the background with a notification dot so the user's current view is not interrupted; set focus to true to switch the user to it.",
        ),
      view: viewSchema.describe("View to open."),
      focus: z
        .boolean()
        .default(false)
        .describe(
          "Whether to switch the user's active view to this tab. Leave false (the default) to reveal work in the background without interrupting the user. Set true when the user should land on the view: a direct navigation request, or the first reveal of the primary object they asked you to build, edit, or produce (the page or site you are working on, a drawing, a spreadsheet). Keep it false for later edits and for secondary or unrelated surfaces.",
        ),
    }),
    z.object({
      operation: z.literal("close").describe("Close an existing open tab."),
      tabId: z
        .string()
        .min(1)
        .describe("ID of the open tab to close. Read this from tab context."),
    }),
  ])
  .describe("ReploOpencodeHarnessManageTabsToolInput");

export const reploOpencodeHarnessManageTabsToolArgsShape = {
  operation: z
    .union([
      z
        .literal("open")
        .describe(
          "Open the selected view. By default it opens in the background with a notification dot so the user's current view is not interrupted; set focus to true to switch the user to it.",
        ),
      z.literal("close").describe("Close an existing open tab."),
    ])
    .describe("Operation to perform."),
  view: viewSchema.optional().describe('Required when operation is "open".'),
  focus: z
    .boolean()
    .optional()
    .describe(
      'Only for operation "open". Defaults to false, which opens the tab in the background without stealing focus. Set true when the user should land on the view: a direct navigation request, or the first reveal of the primary object they asked you to build, edit, or produce.',
    ),
  tabId: z
    .string()
    .min(1)
    .optional()
    .describe('Required when operation is "close".'),
};

export const reploOpencodeHarnessManageTabsToolOutputSchema = z
  .string()
  .describe("ReploOpencodeHarnessManageTabsToolOutput");

const reploOpencodeHarnessRegistrySelectorSchema = z
  .object({
    entrypointPath: z
      .string()
      .min(1)
      .describe(
        "Project-root-relative path to one registry-publishable file. README.md → site, page.tsx → page, SKILL.md → skill, apps/brand-studio/drafts/<slug>/BUSINESS.md → branding, .plan.md/.plan.mdx or markdown under .plans/ → plan, any other .tsx/.jsx → section. The tool reads the file's frontmatter to derive type, slug, name, category, and tags, auto-expands sites and pages to include children, and includes the full parent directory for skills and branding.",
      ),
    overrides: z
      .object({
        hostRoute: z
          .string()
          .min(1)
          .optional()
          .describe(
            "Override the section's preview-capture route. Use when the section has no enclosing page in the project (e.g. publishing a section in isolation that needs a synthetic preview route).",
          ),
        displayName: z
          .string()
          .min(1)
          .optional()
          .describe(
            "Override the user-facing registry item name for share flows where the caller already knows the clean product name. Does not affect slug, category, tags, or routing.",
          ),
      })
      .optional()
      .describe(
        "Operational overrides the file itself can't describe. Slug, category, and tags live in source frontmatter — edit the file, don't override here.",
      ),
  })
  .describe("ReploOpencodeHarnessRegistrySelector");

export const reploOpencodeHarnessUploadRegistryItemsToolInputSchema = z
  .object({
    projectRoot: z
      .string()
      .min(1)
      .nullable()
      .default(null)
      .describe(
        "Absolute or workspace-relative Next.js project root — the site you're targeting (e.g. `/workspace/dev/<directory>`). In the sandbox, defaults to the active site via PROJECT_WEBSITE_DIR; required on local. All selectors in one call must live under this root.",
      ),
    selectors: z
      .array(reploOpencodeHarnessRegistrySelectorSchema)
      .min(1)
      .max(20)
      .describe(
        "Entrypoint paths the user wants published. The tool auto-expands sites to their pages, and pages to the sections they directly import. The 20-item cap applies to the expanded set.",
      ),
    baseUrl: z
      .url()
      .nullable()
      .default(null)
      .describe(
        "Optional already-running dev-server base URL. Null means the tool decides: in a sandbox, reuse 127.0.0.1:3000; on local, always spawn a fresh server on a random ephemeral port.",
      ),
    visibility: z.enum(["public", "unlisted"]).default("unlisted"),
    types: z
      .array(registryItemTypeSchema)
      .nullable()
      .default(null)
      .describe(
        'Optional allow-list applied AFTER selector expansion. Null = publish everything the selectors expand to. Use e.g. ["page"] to publish just the pages a site expands into, skipping sections and the site row itself. Bundle/preview/upload work is still scoped to the kept items.',
      ),
  })
  .describe("ReploOpencodeHarnessUploadRegistryItemsToolInput");

const reploOpencodeHarnessFailedStylesheetUrlSchema = z.object({
  pageKey: z.string().min(1),
  url: z.url(),
  reason: z.string().min(1),
});

const reploOpencodeHarnessRegistryPreviewResultSchema = z.object({
  artifactPath: z.string().min(1),
  id: z.uuid(),
  canonicalPageKey: z.string().min(1),
  canonicalPreviewUrl: z.url(),
  pages: z.array(
    z.object({
      key: z.string().min(1),
      previewUrl: z.url(),
    }),
  ),
  previewHtmlByteLength: z.number(),
  failedStylesheetUrls: z.array(reploOpencodeHarnessFailedStylesheetUrlSchema),
});

export const reploOpencodeHarnessUploadRegistryItemsToolOutputSchema = z
  .object({
    created: z.array(
      z.object({
        id: z.uuid(),
        type: registryItemTypeSchema,
        slug: z.string().min(1),
        sourceType: z.string().nullable(),
        sourceKey: z.string().nullable(),
        url: z.url(),
        shareUrl: z.url().nullable(),
      }),
    ),
    failed: z.array(
      z.object({
        type: registryItemTypeSchema,
        slug: z.string().min(1),
        error: z.string().min(1),
        errorDetail: z.string().min(1).optional(),
      }),
    ),
    previewResults: z.array(reploOpencodeHarnessRegistryPreviewResultSchema),
    timings: z.object({
      bundleMs: z.number(),
      previewMs: z.number(),
      createMs: z.number(),
      totalMs: z.number(),
    }),
  })
  .describe("ReploOpencodeHarnessUploadRegistryItemsToolOutput");
